import apiClient from '../axios';
import { RewardConfigRequest, CreatorConfigRequest, EcpmConfigRequest } from '../types/Reward.api.type';

/**
 * 리워드 어드민 API 모음
 * - BE 응답 형태: { bizStatusCode, bizStatusMessage, results: T }
 * - queryFn에서 반드시 res.data.results 사용할 것
 */
const ADMIN_REWARD_URL = () => '/api/admin/reward';

export const rewardApi = {
  // ==================== 현황 ====================

  /** 현황 요약 */
  getSummary: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/summary`);
  },

  /** 지급 히스토리 */
  getHistory: (page: number, size: number, userId?: number) => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/history`, {
      params: { page, size, ...(userId != null && { userId }) },
    });
  },

  // ==================== 설정 CRUD ====================

  /** 설정 목록 조회 */
  getConfigs: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/configs`);
  },

  /** 설정 추가 */
  createConfig: (params: RewardConfigRequest) => {
    return apiClient.post(`${ADMIN_REWARD_URL()}/configs`, params);
  },

  /** 설정 수정 */
  updateConfig: (id: number, params: RewardConfigRequest) => {
    return apiClient.put(`${ADMIN_REWARD_URL()}/configs/${id}`, params);
  },

  /** 설정 삭제 */
  deleteConfig: (id: number) => {
    return apiClient.delete(`${ADMIN_REWARD_URL()}/configs/${id}`);
  },

  /** 설정 변경 이력 */
  getConfigHistory: (page: number, size: number) => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/configs/history`, { params: { page, size } });
  },

  // ==================== 조각 경제 대시보드 ====================

  /** 조각 경제 현황 */
  getPieceEconomy: (hourlyMode: string = 'TODAY') => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/piece-economy?hourlyMode=${hourlyMode}`);
  },

  /** 리워드 통계 (통화별 집계) */
  getRewardStats: (period: string = '30D') => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/stats`, { params: { period } });
  },

  /** 활동 추이 (투표/출석 등) */
  getActivityTrend: (type: string, period: string = '30D') => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/activity-trend`, { params: { type, period } });
  },

  /** 일별 조각수령 — 유저별 출처 분해 (period: 1D/3D/7D) */
  getDailyPieceBreakdown: (period: string = '1D') => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/daily-piece-breakdown`, { params: { period } });
  },

  // ==================== 환전 히스토리 ====================

  /** 환전 히스토리 */
  getExchangeHistory: (page: number, size: number, userId?: number) => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/exchange-history`, {
      params: { page, size, ...(userId != null && { userId }) },
    });
  },

  // ==================== 작성자 리워드 설정 ====================

  /** 작성자 리워드 설정 목록 */
  getCreatorConfigs: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/creator-config`);
  },

  /** 작성자 리워드 설정 추가 */
  createCreatorConfig: (params: CreatorConfigRequest) => {
    return apiClient.post(`${ADMIN_REWARD_URL()}/creator-config`, params);
  },

  /** 작성자 리워드 설정 수정 */
  updateCreatorConfig: (id: number, params: CreatorConfigRequest) => {
    return apiClient.put(`${ADMIN_REWARD_URL()}/creator-config/${id}`, params);
  },

  /** 작성자 리워드 설정 삭제 */
  deleteCreatorConfig: (id: number) => {
    return apiClient.delete(`${ADMIN_REWARD_URL()}/creator-config/${id}`);
  },

  // ==================== 수익성 분석 + eCPM ====================

  /** 수익성 분석 */
  getProfitability: (period: string = '7D') => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/profitability`, { params: { period } });
  },

  /** eCPM 이력 조회 */
  getEcpmHistory: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/ecpm`);
  },

  /** eCPM 저장 */
  saveEcpm: (params: EcpmConfigRequest) => {
    return apiClient.post(`${ADMIN_REWARD_URL()}/ecpm`, params);
  },

  /** eCPM 삭제 */
  deleteEcpm: (id: number) => {
    return apiClient.delete(`${ADMIN_REWARD_URL()}/ecpm/${id}`);
  },

  /** 분석 정책 조회 (S6-23) */
  getAnalysisConfig: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/analysis-config`);
  },

  /** 분석 정책 변경 (S6-23) */
  updateAnalysisConfig: (params: { analysisPolicy?: string; aiEnabled?: boolean; pieceCost?: number }) => {
    return apiClient.put(`${ADMIN_REWARD_URL()}/analysis-config`, params);
  },

  /** 리워드 타입 라벨 조회 */
  getLabels: () => {
    return apiClient.get(`${ADMIN_REWARD_URL()}/labels`);
  },

  /** 리워드 타입 라벨 수정 */
  updateLabel: (params: { rewardType: string; label: string }) => {
    return apiClient.put(`${ADMIN_REWARD_URL()}/labels`, params);
  },
};

// ==================== 사용자용 API ====================

const REWARD_URL = () => '/api/reward';

export const userRewardApi = {
  /** 포인트 잔액 조회 */
  getPointBalance: () => {
    return apiClient.get(`${REWARD_URL()}/point`);
  },

  /** 오늘 현황 */
  getStatus: () => {
    return apiClient.get(`${REWARD_URL()}/status`);
  },

  /** 지급 내역 */
  getHistory: (page: number = 0, size: number = 20) => {
    return apiClient.get(`${REWARD_URL()}/history`, { params: { page, size } });
  },

  /** 일일 보너스 수령 */
  claimDailyBonus: () => {
    return apiClient.post(`${REWARD_URL()}/daily-bonus`);
  },

  /** 주간 마일스톤 수령 */
  claimWeeklyMilestone: (milestone: number) => {
    return apiClient.post(`${REWARD_URL()}/weekly-milestone`, { milestone });
  },

  /** 투표 리워드 지급 */
  claimVoteReward: (questionId: number) => {
    return apiClient.post(`${REWARD_URL()}/vote`, { questionId });
  },

  /** 내가 작성한 설문 전체 마일스톤 현황 (일괄) */
  getMyCreatorMilestones: () => {
    return apiClient.get(`${REWARD_URL()}/my-creator-milestones`);
  },

  /** 작성자 마일스톤 수동 수령 */
  claimCreatorMilestone: (questionId: number) => {
    return apiClient.post(`${REWARD_URL()}/claim-creator-milestone/${questionId}`);
  },

  /** 분석 해제 (광고 시청 후) */
  unlockAnalysis: (questionId: number) => {
    return apiClient.post(`${REWARD_URL()}/analysis-unlock`, { questionId, platform: 'WEB' });
  },

  /** 광고 제거 상태 조회 */
  getAdFreeStatus: (questionId?: number) => {
    return apiClient.get(`${REWARD_URL()}/ad-free-status`, {
      params: questionId ? { questionId } : {},
    });
  },

  /** 무료 분석 조회 기록 (fire-and-forget) */
  analysisFreeView: (questionId: number) => {
    return apiClient.post(`${REWARD_URL()}/analysis-free-view`, { questionId, platform: 'WEB' });
  },

  // S6-27: 리더보드
  getLeaderboard: (periodType = 'WEEKLY') => apiClient.get('/api/leaderboard', { params: { periodType } }),
  getLiveLeaderboard: (periodType = 'WEEKLY') => apiClient.get('/api/leaderboard/live', { params: { periodType } }),
  getLeaderboardTiers: (periodType = 'WEEKLY_VOTE') =>
    apiClient.get('/api/leaderboard/tiers', { params: { periodType } }),
  getLeaderboardRewards: () => apiClient.get('/api/leaderboard/rewards'),
  claimLeaderboardReward: (snapshotId: number) => apiClient.post(`/api/leaderboard/rewards/${snapshotId}/claim`),
  // 리더보드 어드민
  getLeaderboardConfigs: () => apiClient.get('/api/admin/leaderboard/config'),
  createLeaderboardConfig: (data: any) => apiClient.post('/api/admin/leaderboard/config', data),
  updateLeaderboardConfig: (configId: number, data: any) =>
    apiClient.put(`/api/admin/leaderboard/config/${configId}`, data),
  triggerWeeklyAggregate: () => apiClient.post('/api/admin/leaderboard/aggregate/weekly'),

  // S6-14/20: 출석
  getAttendanceStatus: () => apiClient.get(`${REWARD_URL()}/attendance/status`),
  checkAttendance: () => apiClient.post(`${REWARD_URL()}/attendance/check`),
  saveAttendanceStreak: () => apiClient.post(`${REWARD_URL()}/attendance/streak-save`),
};
